import { ComponentProps, useEffect } from 'react';
import { ParsedUrlQuery } from 'querystring';
import {
    OptimizelyProvider,
    ReactSDKClient,
    enums
} from '@optimizely/react-sdk';
import { setLogLevel, UserAttributes } from '@optimizely/optimizely-sdk';
import triggerTracking from '@blueprint/tracking';
import {
    OptimizelyOverrides,
    getOptimizelyOverrides
} from './optimizelyOverrides';
import { getOptimizelyReactSDKClient } from './optimizelyClientInstance';
import { getDataFile } from './optimizelyDataFile';

export type OptimizelyUserInfo = ComponentProps<
    typeof OptimizelyProvider
>['user'];

type OptimizelyServerRequest = {
    featureKey: string;
    cookies: Record<string, string | undefined>;
    query: ParsedUrlQuery;
    userId?: string;
    userAgent?: string;
    overrideAttributes?: UserAttributes;
};

export const getOptimizelyFeatureFlag = (
    featureFlagName: string,
    featureFlagOverrides: OptimizelyOverrides['features'],
    optimizelyClient: ReactSDKClient | null
): boolean => {
    if (featureFlagName in featureFlagOverrides) {
        return featureFlagOverrides[featureFlagName];
    }

    return optimizelyClient?.isFeatureEnabled(featureFlagName) ?? false;
};

export const getOptimizelyFeatureFlagBooleanVariable = (
    featureFlagName: string,
    variableKey: string,
    optimizelyClient: ReactSDKClient | null
): boolean | null => {
    return (
        optimizelyClient?.getFeatureVariableBoolean(
            featureFlagName,
            variableKey
        ) ?? null
    );
};

export const getOptimizelyUserInfo = (
    anonId: string,
    userAgent: string | undefined
): OptimizelyUserInfo => {
    return {
        id: anonId,
        attributes: {
            $opt_user_agent: userAgent
        }
    };
};

export const useTrackOptimizelyFeature = (
    feature: string,
    featureFlagOverrides: OptimizelyOverrides['features'],
    optimizelyClient: ReactSDKClient | null,
    featureEnabledLabel = 'alternative'
) => {
    const isFeatureEnabled = getOptimizelyFeatureFlag(
        feature,
        featureFlagOverrides,
        optimizelyClient
    );

    useEffect(() => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory: 'optimizely_feature',
            eventAction: feature.toString(),
            eventLabel: isFeatureEnabled ? featureEnabledLabel : 'control'
        });
    }, [feature, featureEnabledLabel, isFeatureEnabled]);

    return isFeatureEnabled;
};

/**
 * This method is for us to be able to get optimizely information on the server
 * we need to get the optimizely SDK and override the user id, since otherwise
 * it loads with an `undefined` user id and it doesn't activate the flag
 *
 * To get the required variables you can use the following in your `getServerSideProps`:
 *
```
const getServerSideProps: GetServerSideProps = async ({
    req,
    res,
    query
}) => {
    const allCookies = cookies.get({ req });
    const anonId = getAnonId({ req });

    const isFeatureEnabled = await getOptimizelyFeatureFlagOnServer({
        featureKey: 'your-feature-key',
        cookies: allCookies,
        query,
        userId: anonId,
        userAgent: req.headers['user-agent']
    });

    if (!isFeatureEnabled) {
        return {
            redirect: {
                destination: `/your-redirect/`,
                permanent: false
            }
        };
    }
}
```
*/
export const getOptimizelyFeatureFlagOnServer = async ({
    featureKey,
    cookies,
    query,
    userId,
    userAgent,
    overrideAttributes
}: OptimizelyServerRequest) => {
    const optimizelyOverrides = getOptimizelyOverrides(cookies, query);
    if (featureKey in optimizelyOverrides.features) {
        return optimizelyOverrides.features[featureKey];
    }

    const dataFile = await getDataFile();
    const optimizely = getOptimizelyReactSDKClient(dataFile || {});

    return (
        // eslint-disable-next-line no-restricted-properties
        optimizely?.isFeatureEnabled(featureKey, userId, {
            $opt_user_agent: userAgent,
            ...overrideAttributes
        }) ?? false
    );
};

// Logging docs:
// https://docs.developers.optimizely.com/full-stack/docs/customize-logger-javascript
const isDev = process.env.NODE_ENV !== 'production';
if (isDev) {
    // Change here to increase logging levels in development
    setLogLevel(enums.LOG_LEVEL.WARNING);
}
